import React, { memo, useCallback, useMemo } from 'react'
import tw from 'twin.macro'
import { CirclePlusOutlineMinor, ExportMinor } from '@shopify/polaris-icons'
import { Modal, Typography } from '@mui/material'
import { colors } from 'src/theme'
import NotFoundImage from 'src/assets/images/not-found.png'
import { Button } from 'src/components/shared/Button'
import { ModalHeader } from 'src/components/shared/ModalHeader'

interface Props {
  isOpen?: boolean
  onClose(): void
  onOpenUploadPaymentsModal(): void
  onOpenAddSinglePaymentModal(): void
}

const NothingToFundModalComponent = ({
  isOpen,
  onClose,
  onOpenUploadPaymentsModal,
  onOpenAddSinglePaymentModal,
}: Props) => {
  const container = useMemo(() => document.getElementById('root'), [])

  const handleAddSinglePayment = useCallback(() => {
    onClose()
    onOpenAddSinglePaymentModal()
  }, [onClose, onOpenAddSinglePaymentModal])

  const handleUploadCSV = useCallback(() => {
    onClose()
    onOpenUploadPaymentsModal()
  }, [onClose, onOpenUploadPaymentsModal])

  return (
    <Modal
      container={container}
      open={Boolean(isOpen)}
      onClose={onClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
      css={tw`flex justify-center items-center backdrop-blur p-4`}
    >
      <div css={tw`bg-white rounded-lg overflow-hidden w-[330px]`}>
        <ModalHeader onClose={onClose} className={tw`border-0 pb-0`} />
        <div css={tw`px-4 pt-0 pb-4 flex flex-col items-center`}>
          <div css={tw`flex justify-center`}>
            <img
              src={NotFoundImage}
              alt="Confirmation"
              css={tw`w-[210px] h-[164px] object-contain`}
            />
          </div>
          <Typography variant="h4" css={tw`text-center`}>
            Nothing to fund!
          </Typography>
          <Typography variant="body2" color={colors.SHADES_GREY_400} css={tw`text-center mt-0.5`}>
            Add a payment or upload in bulk to fund eligible payments. Only payments with the status
            "Requires funding" will be affected.
          </Typography>
        </div>
        <div
          css={tw`px-4 py-3 flex flex-col gap-2 justify-between items-center border-0 border-t border-solid border-[#EDEDED]`}
        >
          <Button
            fullWidth
            size="small"
            color="light"
            leftIcon={<CirclePlusOutlineMinor width={20} fill={colors.PRIMARY_PURPLE_500} />}
            onClick={handleAddSinglePayment}
          >
            Add single payment
          </Button>
          <Button
            fullWidth
            size="small"
            color="light"
            onClick={handleUploadCSV}
            leftIcon={<ExportMinor width={20} fill={colors.PRIMARY_PURPLE_500} />}
          >
            Upload CSV file
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export const NothingToFundModal = memo(NothingToFundModalComponent)
